import type { StoryMediaPlatform } from 'types/story';
import { EventEmitter } from 'utils/event-emitter';
import { ZOOM_MIN, ZOOM_STEP } from '../../DimensionControls/constants';

export type ResizerSetDisabledEvent = { type: 'setDisabled'; data: boolean };

export type ResizerSetCardHeightEvent = { type: 'setCardHeight'; data: number };

export type ResizerMousedownEvent = { type: 'resizerMousedown'; data: { pageX: number } };

type ResizerEvent = ResizerSetDisabledEvent | ResizerSetCardHeightEvent | ResizerMousedownEvent;

export const ResizerEvEm = new EventEmitter<ResizerEvent>();

interface GetInitialPlatformWidthProps {
	platform: StoryMediaPlatform;
	min: number;
	max: number;
	containerWidth: number;
}

export function getInitialPlatformWidth(props: GetInitialPlatformWidthProps): string {
	const containerOffset = 0;

	switch (props.platform) {
		case 'desktop':
		case 'tablet':
			return `${Math.max(props.min, Math.min(props.containerWidth - containerOffset, props.max))}px`;
		case 'mobileLandscape':
			return `${Math.min(667, props.max)}px`;
		case 'mobile':
			return `${Math.min(375, props.max)}px`;
		default:
			return '';
	}
}

export function getLogicalCanvasSize({
	requestedWidth,
	boundaries,
	ratio,
}: {
	requestedWidth: number;
	boundaries: { min: number; max: number };
	ratio: number;
}) {
	const width = Math.max(boundaries.min, Math.min(requestedWidth, boundaries.max));

	return { width, height: width / ratio };
}

export function getResizeBoundaries({
	boundaries,
	containerWidth,
}: {
	boundaries: { min: number; max: number };
	containerWidth: number;
}) {
	return {
		min: boundaries.min,
		max: Math.max(boundaries.min, Math.min(boundaries.max, containerWidth)),
	};
}

export function getToggledCanvasWidth({
	currentWidth,
	boundaries,
	containerWidth,
}: {
	currentWidth: number;
	boundaries: { min: number; max: number };
	containerWidth: number;
}) {
	const effectiveBoundaries = getResizeBoundaries({ boundaries, containerWidth });

	return currentWidth === effectiveBoundaries.max ? effectiveBoundaries.min : effectiveBoundaries.max;
}

export const RESIZER_DRAG_THRESHOLD = 3;

export function hasResizerDragStarted(startPageX: number, currentPageX: number) {
	return Math.abs(currentPageX - startPageX) >= RESIZER_DRAG_THRESHOLD;
}

export function getFitZoom({ logicalWidth, containerWidth }: { logicalWidth: number; containerWidth: number }) {
	return logicalWidth ? Math.min(1, containerWidth / logicalWidth) : 1;
}

export function getCenteredScrollLeft({
	scrollLeft,
	logicalWidth,
	previousZoom,
	nextZoom,
	previousContainerWidth,
	nextContainerWidth,
}: {
	scrollLeft: number;
	logicalWidth: number;
	previousZoom: number;
	nextZoom: number;
	previousContainerWidth: number;
	nextContainerWidth: number;
}) {
	const previousStageWidth = logicalWidth * previousZoom;
	const logicalCenter =
		previousStageWidth <= previousContainerWidth
			? logicalWidth / 2
			: (scrollLeft + previousContainerWidth / 2) / previousZoom;
	const targetScrollLeft = logicalCenter * nextZoom - nextContainerWidth / 2;
	const maxScrollLeft = Math.max(0, logicalWidth * nextZoom - nextContainerWidth);

	return Math.max(0, Math.min(targetScrollLeft, maxScrollLeft));
}

export function getZoomOut(zoom: number) {
	return zoom <= ZOOM_MIN ? zoom : Math.max(ZOOM_MIN, zoom - ZOOM_STEP);
}
